
import { useEffect } from 'react';

interface Portfolio {
  id: number;
  title: string;
  description: string;
  category: string;
  client: string;
  year: string;
  image: string;
  tags: string[];
  detailImages?: string[];
}

interface PortfolioModalProps {
  portfolio: Portfolio | null;
  onClose: () => void;
}

export default function PortfolioModal({ portfolio, onClose }: PortfolioModalProps) {
  useEffect(() => {
    if (portfolio) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [portfolio]);

  if (!portfolio) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-5xl w-full my-8 relative animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-neon-pink transition-colors z-10 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="p-8 md:p-12">
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-neon-pink/10 text-neon-pink text-sm font-semibold rounded-full mb-4">
              {portfolio.category}
            </span>
            <h2 className="text-4xl font-bold mb-4">{portfolio.title}</h2>
            <p className="text-gray-600 text-lg mb-6">{portfolio.description}</p>
            
            <div className="flex flex-wrap gap-6 text-sm mb-6">
              <div>
                <span className="text-gray-500">클라이언트:</span>
                <span className="ml-2 font-semibold">{portfolio.client}</span>
              </div>
              <div>
                <span className="text-gray-500">연도:</span>
                <span className="ml-2 font-semibold">{portfolio.year}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {portfolio.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold mb-4">프로젝트 상세 이미지</h3>
            {portfolio.detailImages && portfolio.detailImages.length > 0 ? (
              <div className="space-y-6">
                {portfolio.detailImages.map((image, index) => (
                  <div
                    key={index}
                    className="rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
                  >
                    <div className="w-full h-96 bg-gray-100">
                      <img
                        src={image}
                        alt={`${portfolio.title} 상세 이미지 ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-gray-200">
                <div className="w-full h-96 bg-gray-100">
                  <img
                    src={portfolio.image}
                    alt={portfolio.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <a
              href="https://pf.kakao.com/_KkAxon/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-neon-pink text-white px-8 py-3 rounded-full font-semibold hover:bg-neon-pink/90 hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              비슷한 프로젝트 문의하기
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
